// The activity feed and the shell's counts — TODO T36 (2 Sep 2026), design doc § T36.1, .2, .8.
//
// Two polls and one write. The feed is what kadmon's side recorded, read back — notifications,
// replies, voice notes, code redemptions — and the counts are the numbers the shell shows on
// every screen: the nav badges, the unread dot and "kadmon · N replies today". Every number, ONE
// request: each poll is a request per recruiter per interval against a service sized on seats,
// and the shell is on every screen.
//
// THE WRITE MARKS THE FEED READ, AND THAT IS ALL IT CAN DO. `notifications` is agent-owned;
// "mark all read" is a row per notification in the console-owned `notification_reads`, for this
// recruiter — the mirror-table pattern the schema chose at T4 so the brain never touches what
// the worker wrote (packages/db/src/schema/tables.ts, `notificationReads`).
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  ActivityResponse,
  ActivityRow,
  ApproveClientProfileResponse,
  ConsoleCounts,
  CountsResponse,
  CreatedResponse,
} from '@vera/bff';
import { API_PREFIX, query, type RequestFn } from './core.ts';
import { POLL_MS } from './intervals.ts';
import { KEYS, useConsoleMutation } from './mutation.ts';
import { useRequest } from './provider.tsx';

export const activityApi = (request: RequestFn) => ({
  list: async (limit?: number) =>
    (await request<ActivityResponse>(`${API_PREFIX}/activity${query({ limit })}`)).activity,
  /** No body: "all" is the only thing it marks. */
  markAllRead: () => request<void>(`${API_PREFIX}/activity/read`, { method: 'POST' }),
  /** `since` is this browser's midnight — "today" is the recruiter's day, not the server's. */
  counts: (since: string) => request<CountsResponse>(`${API_PREFIX}/counts${query({ since })}`),
  /** T484c: Approve on a candidate's yes to kadmon's help hiring: makes them the company's contact. */
  approveRequest: (requestId: string) =>
    request<ApproveClientProfileResponse>(
      `${API_PREFIX}/client-profile-requests/${requestId}/approve`,
      { method: 'POST' },
    ),
  /** T562: Override on a client contact past the day's research cap: another 3 runs today. */
  overrideCap: (notificationId: string) =>
    request<CreatedResponse>(`${API_PREFIX}/caps/${notificationId}/override`, { method: 'POST' }),
});

/**
 * Midnight where the recruiter is, as an ISO instant. The service counts "replies today" from
 * whatever it is sent, so the day boundary is decided here, once, by the clock of the person
 * reading the number.
 */
export function startOfLocalDay(now: Date = new Date()): string {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
}

export function useActivity(limit?: number): UseQueryResult<ActivityRow[]> {
  const request = useRequest();
  return useQuery({
    queryKey: ['activity', limit],
    queryFn: () => activityApi(request).list(limit),
    refetchInterval: POLL_MS.activity,
  });
}

export function useCounts(): UseQueryResult<ConsoleCounts> {
  const request = useRequest();
  // The key carries the day, so a tab left open overnight starts counting the new day at its
  // next poll rather than adding to yesterday's.
  const since = startOfLocalDay();
  return useQuery({
    queryKey: ['counts', since],
    queryFn: () => activityApi(request).counts(since),
    refetchInterval: POLL_MS.counts,
  });
}

export const useMarkActivityRead = () =>
  useConsoleMutation((request) => activityApi(request).markAllRead(), KEYS.activity);

/** T484c: the alert's Approve; the feed shows it approved, and the people pages the new contact. */
export const useApproveRequest = () =>
  useConsoleMutation(
    (request, requestId: string) => activityApi(request).approveRequest(requestId),
    [...KEYS.activity, ...KEYS.people],
  );

/** T562: the cap alert's Override; the feed shows it overridden, and by whom. */
export const useOverrideCap = () =>
  useConsoleMutation(
    (request, notificationId: string) => activityApi(request).overrideCap(notificationId),
    KEYS.activity,
  );
